/**
 * Multi-view viewer for a workflow node's input / output payload.
 *
 * Inspired by n8n's output panel: four lenses on the same data, each
 * useful in a different scenario.
 *
 * The payload we receive is unstructured — could be a serialised
 * ``list[Item]`` batch (the common case for node I/O), a bare object
 * / array / scalar, or null. The component detects the shape once
 * and adapts each view accordingly.
 *
 * Views:
 *
 *   * **Schema** — tree of keys with type chips. Walks the first item
 *     (or the bare value) and renders nested keys with `:type` badges.
 *     Best for "what does this data look like".
 *
 *   * **Table** — when the payload is a batch of items (or a list of
 *     objects), render one row per item with columns = union of keys.
 *     Nested values are truncated; click reveals the raw value via the
 *     JSON tab. Best for visual scan of N similar rows.
 *
 *   * **JSON** — pretty-printed JSON of the whole payload. The fallback
 *     that always works; matches the previous Result tab content.
 *
 *   * **Binary** — per-item attachments (file name / size / mime).
 *     A download button is rendered when a binary is stored externally
 *     (BlobStorage); inline binaries show a "stored inline" badge.
 *     Empty state when no items carry binary slots.
 *
 * The viewer is read-only; it does not mutate the payload.
 */
import { Braces, Database, Download, FileBox, Loader2, TableProperties } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { base64ToBlob, triggerBlobDownload, workflowExecutionsApi } from "@/lib/api-workflows";
import { JsonTree } from "@/components/workflows/JsonTree";
import { SchemaTree } from "@/components/workflows/SchemaTree";
import { VALUE_FIELD_KEY, filterSchemaFields, inferSchema } from "@/lib/workflows/schemaInfer";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Slim mirror of the backend BinaryRef pydantic model — see
 *  ``hub.workflow.items.BinaryRef``. Kept local so the viewer doesn't
 *  pull workflow-specific types. */
export interface BinaryRef {
  mode: "inline" | "external";
  data?: string | null;
  blob_id?: string | null;
  mime_type: string;
  file_name?: string | null;
  file_extension?: string | null;
  file_size?: number | null;
}

/** Serialised Item — what comes back in `output_payload` for nodes
 *  that follow the batch convention. The `json` alias is the one used
 *  on the wire (see ``Item.data`` with `alias="json"`). */
interface SerialisedItem {
  json?: unknown;
  binary?: Record<string, BinaryRef>;
  meta?: Record<string, unknown>;
  paired_item?: unknown;
}

type ViewTab = "schema" | "table" | "json" | "binary";

/** Execution context for downloading binary slots. When provided, the
 *  Binary tab can fetch *external* (blob-backed) slots from the backend
 *  download endpoint. Inline slots download client-side regardless. */
export interface BinaryDownloadContext {
  executionId: string;
  nodeId: string;
  io: "input" | "output";
}

export interface ItemBatchViewerProps {
  payload: unknown;
  /** When omitted, the viewer picks "schema" if there's structured
   *  data, else "json". */
  initialTab?: ViewTab;
  /** Optional className for the outer wrapper. */
  className?: string;
  /** When set, binary slots show a Download button (external slots are
   *  fetched via the per-execution blob endpoint). */
  blobContext?: BinaryDownloadContext;
  /** Stretch to the FULL height of the parent and let each lens scroll
   *  internally (the node-drawer Output pane wants this so the viewer
   *  matches the full-height Input tree). Default `false` keeps the
   *  content-sized behaviour every other call site relies on (ETL config,
   *  run-details, sub-workflow dialog), where the viewer sits inline and
   *  the JSON/Table lenses cap their own height. */
  fill?: boolean;
  /** Expression root the SCHEMA lens binds its fields under (`$json`, an
   *  upstream `$('Label').item.json`). Set only by the node drawer's Input
   *  pane — it turns the Schema lens into the DRAGGABLE binding picker.
   *  Unset ⇒ view-only (every other call site). */
  exprBase?: string;
  /** Click-to-insert a Schema-lens leaf into the last-focused ƒx field. */
  onPickBinding?: (binding: string) => void;
}

// ---------------------------------------------------------------------------
// Shape detection
// ---------------------------------------------------------------------------

/** Heuristic: does this look like a serialised ``list[Item]`` batch?
 *  We don't trust the type; we sniff the shape. */
function isItemBatch(p: unknown): p is SerialisedItem[] {
  if (!Array.isArray(p) || p.length === 0) return false;
  // Every element must be an object with at least the `json` alias OR
  // a `binary` map. The shape is tolerant — older payloads might be
  // missing `paired_item` or `meta`.
  return p.every(
    (x) =>
      typeof x === "object" &&
      x !== null &&
      !Array.isArray(x) &&
      ("json" in x || "binary" in x || "meta" in x),
  );
}

/** Bare list of dicts — table-friendly without being Item-shaped. */
function isObjectArray(p: unknown): p is Array<Record<string, unknown>> {
  if (!Array.isArray(p) || p.length === 0) return false;
  return p.every((x) => typeof x === "object" && x !== null && !Array.isArray(x));
}

/** Normalised view of the payload that all tabs consume. */
interface NormalisedPayload {
  shape: "batch" | "object_array" | "bare";
  /** For batch / object_array: the per-row JSON-side payload. For
   *  bare: a one-element list containing the raw value (so Schema/Table
   *  have something to iterate). */
  rows: unknown[];
  /** For batch only: per-row binary slots. */
  binaries: Array<Record<string, BinaryRef>>;
  itemCount: number;
}

function normalise(payload: unknown): NormalisedPayload {
  if (isItemBatch(payload)) {
    return {
      shape: "batch",
      rows: payload.map((it) => it.json ?? null),
      binaries: payload.map((it) => it.binary ?? {}),
      itemCount: payload.length,
    };
  }
  if (isObjectArray(payload)) {
    return {
      shape: "object_array",
      rows: payload,
      binaries: payload.map(() => ({})),
      itemCount: payload.length,
    };
  }
  return {
    shape: "bare",
    rows: [payload],
    binaries: [{}],
    itemCount: payload == null ? 0 : 1,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ItemBatchViewer({
  payload,
  initialTab,
  className,
  blobContext,
  fill = false,
  exprBase,
  onPickBinding,
}: ItemBatchViewerProps) {
  const data = React.useMemo(() => normalise(payload), [payload]);

  // Search over the STRUCTURED lenses: Schema filters fields by name (keeping
  // ancestors of a match), Table filters rows by cell content. JSON/Binary are
  // deliberately unfiltered — the JSON tree is where you read a whole value,
  // and a filtered tree would misrepresent the payload's real shape.
  const [query, setQuery] = React.useState("");

  // Default tab: schema when there's structured content, else json.
  // The user can switch freely after.
  const defaultTab: ViewTab = React.useMemo(() => {
    if (initialTab) return initialTab;
    if (data.shape === "batch" || data.shape === "object_array") return "schema";
    if (data.rows[0] != null && typeof data.rows[0] === "object") return "schema";
    return "json";
  }, [data, initialTab]);

  const [tab, setTab] = React.useState<ViewTab>(defaultTab);

  // Re-pick the default tab when the payload changes shape between
  // runs (e.g. moving from a node that emits items to one that emits
  // null) — keeps the user from staring at an empty "Binary" tab.
  React.useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  const hasAnyBinary = data.binaries.some((b) => Object.keys(b).length > 0);

  if (payload == null) {
    return (
      <div
        className={cn(
          "rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-[11px] text-muted-foreground",
          className,
        )}
      >
        No payload to display.
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", fill && "h-full min-h-0", className)}>
      {/* Sub-tabs — compact icon+label strip, n8n-style */}
      <div className="flex items-center justify-between border-b border-border/60">
        <div className="flex gap-0">
          {[
            { id: "schema" as const, label: "Schema", icon: Database },
            { id: "table" as const, label: "Table", icon: TableProperties },
            { id: "json" as const, label: "JSON", icon: Braces },
            { id: "binary" as const, label: "Binary", icon: FileBox },
          ].map(({ id, label, icon: Icon }) => {
            // Binary tab is dimmed when no binaries are present — the
            // user can still click it (sees the empty state) but the
            // visual hint avoids a wasted click.
            const dimmed = id === "binary" && !hasAnyBinary;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  "inline-flex items-center gap-1.5 border-b-2 px-2.5 py-1.5 text-[11px] capitalize transition",
                  tab === id
                    ? "border-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                  dimmed && "opacity-50",
                )}
              >
                <Icon className="h-3 w-3" strokeWidth={1.75} />
                {label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pr-1">
          {(tab === "schema" || tab === "table") && data.itemCount > 0 && (
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca…"
              aria-label="Cerca nei dati"
              className="h-5 w-28 rounded border border-border/60 bg-transparent px-1.5 text-[10px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          )}
          <span className="text-[10px] text-muted-foreground">
            {data.itemCount} {data.itemCount === 1 ? "item" : "items"}
          </span>
        </div>
      </div>

      <div className={cn("min-h-0", fill ? "flex-1 overflow-hidden" : "")}>
        {tab === "schema" && (
          <SchemaView
            data={data}
            fill={fill}
            query={query}
            exprBase={exprBase}
            onPickBinding={onPickBinding}
          />
        )}
        {tab === "table" && <TableView data={data} fill={fill} query={query} />}
        {tab === "json" && <JsonView payload={payload} fill={fill} />}
        {tab === "binary" && <BinaryView data={data} blobContext={blobContext} fill={fill} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schema view — tree of keys with type chips
// ---------------------------------------------------------------------------

function SchemaView({
  data,
  fill = false,
  query = "",
  exprBase,
  onPickBinding,
}: {
  data: NormalisedPayload;
  fill?: boolean;
  query?: string;
  exprBase?: string;
  onPickBinding?: (binding: string) => void;
}) {
  // The UNION of every item, not item #0 — a field the first item happens to
  // lack used to be invisible here, which is the opposite of what a schema is
  // for. See lib/workflows/schemaInfer.
  const schema = React.useMemo(() => inferSchema(data.rows), [data.rows]);
  const shown = React.useMemo(
    () => (query.trim() ? { ...schema, fields: filterSchemaFields(schema.fields, query) } : schema),
    [schema, query],
  );
  return (
    <div
      className={cn(
        "rounded border border-border/60 bg-muted/30 p-2",
        fill && "h-full overflow-auto",
      )}
    >
      <SchemaTree schema={shown} exprBase={exprBase} onPick={onPickBinding} />
      {query.trim() !== "" && shown.fields.length === 0 && schema.fields.length > 0 && (
        <div className="mt-1 text-[10px] text-muted-foreground">
          Nessun campo corrisponde a «{query.trim()}».
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table view — one row per item
// ---------------------------------------------------------------------------

/** Row-count options for the Table lens pager. */
const TABLE_PAGE_SIZES = [10, 25, 50, 100] as const;

function TableView({
  data,
  fill = false,
  query = "",
}: {
  data: NormalisedPayload;
  fill?: boolean;
  query?: string;
}) {
  // An item's data needn't be an object — Split Out emits the bare element, so
  // a batch is routinely `[1, 2, 3]`. Those items have no columns, but they DO
  // have values: wrap each in a one-column row rather than showing the "needs
  // objects" dead end while the JSON lens plainly lists them. (Same reasoning
  // as schemaInfer's VALUE_FIELD_KEY — keep the two lenses consistent.)
  const isScalarBatch =
    data.rows.length > 0 &&
    !data.rows.some((r) => r !== null && typeof r === "object" && !Array.isArray(r));

  const allRows = (
    isScalarBatch
      ? data.rows.map((v) => ({ [VALUE_FIELD_KEY]: v }))
      : data.rows.filter((r) => r !== null && typeof r === "object" && !Array.isArray(r))
  ) as Array<Record<string, unknown>>;

  // Search filters ROWS by cell content (any column, case-insensitive). Nested
  // values match on their JSON text — cheap, and "the row that mentions X" is
  // the question a table search answers.
  const q = query.trim().toLowerCase();
  const objectRows = q
    ? allRows.filter((r) =>
        Object.values(r).some((v) => {
          if (v == null) return false;
          const s = typeof v === "object" ? JSON.stringify(v) : String(v);
          return s.toLowerCase().includes(q);
        }),
      )
    : allRows;

  // Client-side pagination — the payload is already fully in memory (node
  // I/O is a bounded batch), so this is a pure render-window over the rows.
  // Keeps a 1000-item transform output from painting one giant table.
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState(0);
  // Reset to the first page whenever the underlying data OR the search
  // changes (a new run, a different node, a narrower row set) so we never
  // land on an out-of-range page.
  React.useEffect(() => {
    setPage(0);
  }, [data, q]);

  if (objectRows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-[11px] text-muted-foreground">
        {q && allRows.length > 0
          ? `Nessuna riga corrisponde a «${query.trim()}».`
          : "Nessuna riga da mostrare."}
      </div>
    );
  }

  // Build the column set as a stable union, ordered by first appearance.
  const cols: string[] = [];
  const seen = new Set<string>();
  for (const r of objectRows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }
  }

  const pageCount = Math.max(1, Math.ceil(objectRows.length / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * pageSize;
  const pageRows = objectRows.slice(start, start + pageSize);
  const showPager = objectRows.length > TABLE_PAGE_SIZES[0];

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", fill && "h-full")}>
      <div
        className={cn("overflow-auto rounded border border-border/60", fill && "min-h-0 flex-1")}
      >
        <table className="w-full border-collapse text-[11px]">
          <thead className="sticky top-0 z-10 bg-muted/40">
            <tr>
              <th className="border-b border-border/60 bg-muted/40 px-2 py-1 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                #
              </th>
              {cols.map((c) => (
                <th
                  key={c}
                  className="border-b border-border/60 bg-muted/40 px-2 py-1 text-left text-[10px] uppercase tracking-wider text-muted-foreground"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => {
              const rowIndex = start + i;
              return (
                <tr key={rowIndex} className="even:bg-muted/20">
                  <td className="border-b border-border/40 px-2 py-1 font-mono text-muted-foreground">
                    {rowIndex}
                  </td>
                  {cols.map((c) => (
                    <td
                      key={c}
                      className="border-b border-border/40 px-2 py-1 align-top font-mono"
                      title={
                        c in r && r[c] != null && typeof r[c] === "object"
                          ? JSON.stringify(r[c])
                          : undefined
                      }
                    >
                      {renderCell(r[c])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPager && (
        <div className="flex shrink-0 items-center justify-between gap-2 text-[10px] text-muted-foreground">
          <span>
            {start + 1}–{Math.min(start + pageSize, objectRows.length)} of {objectRows.length}
          </span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              <span>Rows</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
                className="h-6 rounded border border-border/60 bg-transparent px-1 text-[10px]"
              >
                {TABLE_PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
                className="rounded border border-border/60 px-1.5 py-0.5 transition enabled:hover:text-foreground disabled:opacity-40"
              >
                Prev
              </button>
              <span className="font-mono">
                {clampedPage + 1}/{pageCount}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={clampedPage >= pageCount - 1}
                className="rounded border border-border/60 px-1.5 py-0.5 transition enabled:hover:text-foreground disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderCell(v: unknown): React.ReactNode {
  if (v === null || v === undefined) {
    return <span className="text-muted-foreground italic">—</span>;
  }
  if (typeof v === "boolean") {
    return v ? (
      <span className="text-success">true</span>
    ) : (
      <span className="text-muted-foreground">false</span>
    );
  }
  if (typeof v === "object") {
    const json = JSON.stringify(v);
    const preview = Array.isArray(v) ? `[…${v.length}]` : `{${Object.keys(v).length}}`;
    return (
      <span className="text-muted-foreground" title={json}>
        {preview}
      </span>
    );
  }
  const s = String(v);
  return s.length > 40 ? <span title={s}>{s.slice(0, 37)}…</span> : s;
}

// ---------------------------------------------------------------------------
// JSON view — pretty-print
// ---------------------------------------------------------------------------

function JsonView({ payload, fill = false }: { payload: unknown; fill?: boolean }) {
  // A collapsible, coloured tree — NOT `<pre>{JSON.stringify(…)}</pre>`. A run's
  // payload is routinely a 500-item batch; flat text can't be read, only
  // scrolled. See JsonTree.
  return (
    <div
      className={cn(
        "overflow-auto rounded border border-border/60 bg-muted/30 p-2",
        // Fill the pane (drawer Output) vs. self-cap inline (dialogs/config).
        fill ? "h-full" : "max-h-[40vh]",
      )}
    >
      <JsonTree value={payload} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Binary view — list per-item attachments
// ---------------------------------------------------------------------------

function BinaryView({
  data,
  blobContext,
  fill = false,
}: {
  data: NormalisedPayload;
  blobContext?: BinaryDownloadContext;
  fill?: boolean;
}) {
  const total = data.binaries.reduce((acc, b) => acc + Object.keys(b).length, 0);
  if (total === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-center text-[11px] text-muted-foreground">
        No binary attachments on this payload.
      </div>
    );
  }
  return (
    <div className={cn("space-y-3", fill && "h-full overflow-auto")}>
      {data.binaries.map((slots, i) => {
        const keys = Object.keys(slots);
        if (keys.length === 0) return null;
        return (
          <div key={i} className="rounded border border-border/60 bg-muted/20 p-2 text-[11px]">
            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              Item #{i}
            </div>
            <div className="space-y-2">
              {keys.map((slot) => (
                <BinarySlotCard
                  key={slot}
                  slot={slot}
                  ref_={slots[slot]}
                  itemIndex={i}
                  blobContext={blobContext}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BinarySlotCard({
  slot,
  ref_,
  itemIndex,
  blobContext,
}: {
  slot: string;
  ref_: BinaryRef;
  itemIndex: number;
  blobContext?: BinaryDownloadContext;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Inline slots carry their bytes in the payload → download with zero
  // network. External slots need the backend endpoint, which requires
  // an execution context (executionId + nodeId + io).
  const inlineReady = ref_.mode === "inline" && !!ref_.data;
  const externalReady = ref_.mode === "external" && !!blobContext;
  const canDownload = inlineReady || externalReady;
  const downloadName = ref_.file_name || `${slot}${ref_.file_extension ?? ""}`;

  async function onDownload() {
    setError(null);
    setBusy(true);
    try {
      if (inlineReady) {
        triggerBlobDownload(base64ToBlob(ref_.data as string, ref_.mime_type), downloadName);
      } else if (externalReady && blobContext) {
        await workflowExecutionsApi.downloadNodeBlob(blobContext.executionId, {
          node: blobContext.nodeId,
          slot,
          item: itemIndex,
          io: blobContext.io,
          suggestedName: downloadName,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded border border-border/40 bg-background/50 p-2">
      <div className="mb-1 flex items-center gap-1.5">
        <code className="font-mono text-[11px] font-semibold text-accent">{slot}</code>
        <Badge tone="neutral" className="text-[9px]">
          {ref_.mode}
        </Badge>
        <button
          type="button"
          onClick={onDownload}
          disabled={!canDownload || busy}
          title={
            canDownload
              ? "Download file"
              : ref_.mode === "external"
                ? "Download needs an execution context (open from a run)"
                : "Inline data unavailable (payload truncated)"
          }
          className={cn(
            "ml-auto inline-flex items-center gap-1 rounded border border-border/60 px-1.5 py-0.5 text-[10px] transition",
            canDownload ? "hover:bg-accent/10 hover:text-accent" : "cursor-not-allowed opacity-40",
          )}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download
        </button>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-[11px]">
        {ref_.file_name && (
          <>
            <dt className="text-muted-foreground">File name</dt>
            <dd className="font-mono">{ref_.file_name}</dd>
          </>
        )}
        {ref_.file_extension && (
          <>
            <dt className="text-muted-foreground">Extension</dt>
            <dd className="font-mono">{ref_.file_extension}</dd>
          </>
        )}
        <dt className="text-muted-foreground">MIME type</dt>
        <dd className="font-mono">{ref_.mime_type}</dd>
        {typeof ref_.file_size === "number" && (
          <>
            <dt className="text-muted-foreground">Size</dt>
            <dd className="font-mono">{formatBytes(ref_.file_size)}</dd>
          </>
        )}
      </dl>
      {error && <div className="mt-1 text-[10px] text-danger">{error}</div>}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
